/** App Router ISR paths on the public site — use /api/revalidate-path. */
const APP_ROUTER_PATH_LIST = [
  '/es',
  '/en',
  '/es/blog',
  '/en/blog',
  '/es/sitemap',
  '/en/sitemap',
  '/es/aviso-privacidad',
  '/en/privacy-policy',
  '/es/publicidad',
  '/en/advertise',
  '/es/descargar-app',
  '/en/download-app',
];

const APP_ROUTER_PATHS = new Set(APP_ROUTER_PATH_LIST);

const DEFAULT_BATCH_SIZE = 5;

function normalizePath(path) {
  if (!path || typeof path !== 'string') return '';
  const trimmed = path.trim();
  if (!trimmed) return '';
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/\/+$/, '') || '/';
}

/**
 * Revalidate a public URL.
 * - App homes (/es, /en, …) → /api/revalidate-path (revalidatePath)
 * - Entry/country Pages routes → /api/revalidate (res.revalidate)
 */
export async function revalidatePublicPath(publicUrl, secret, path) {
  const normalized = normalizePath(path);
  if (!publicUrl || !secret || !normalized) {
    throw new Error('Missing publicUrl, secret, or path');
  }

  const endpoint = APP_ROUTER_PATHS.has(normalized)
    ? '/api/revalidate-path'
    : '/api/revalidate';

  const res = await fetch(
    `${publicUrl}${endpoint}?secret=${encodeURIComponent(secret)}&path=${encodeURIComponent(normalized)}`,
    { method: 'POST' }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message || `Revalidate failed for ${normalized}`);
  }

  return normalized;
}

/**
 * Revalidate many paths in parallel batches.
 * @param {string} publicUrl
 * @param {string} secret
 * @param {string[]} paths
 * @param {{
 *   batchSize?: number,
 *   onBatchStart?: (info: { paths: string[], batchIndex: number, total: number }) => void,
 *   onPathDone?: (info: { path: string, ok: boolean, error?: string, completed: number, total: number }) => void
 * }} [options]
 */
export async function revalidatePublicPathsInBatches(
  publicUrl,
  secret,
  paths,
  options = {}
) {
  const batchSize = Math.max(1, Number(options.batchSize) || DEFAULT_BATCH_SIZE);
  const onBatchStart = typeof options.onBatchStart === 'function' ? options.onBatchStart : null;
  const onPathDone = typeof options.onPathDone === 'function' ? options.onPathDone : null;

  const unique = [
    ...new Set(
      (Array.isArray(paths) ? paths : [])
        .map(normalizePath)
        .filter(Boolean)
    ),
  ];

  let completed = 0;
  let ok = 0;
  let failed = 0;
  const failures = [];

  for (let i = 0; i < unique.length; i += batchSize) {
    const batch = unique.slice(i, i + batchSize);
    onBatchStart?.({
      paths: batch,
      batchIndex: Math.floor(i / batchSize),
      total: unique.length,
    });

    const results = await Promise.allSettled(
      batch.map((path) => revalidatePublicPath(publicUrl, secret, path))
    );

    results.forEach((result, index) => {
      const path = batch[index];
      completed += 1;
      if (result.status === 'fulfilled') {
        ok += 1;
        onPathDone?.({ path, ok: true, completed, total: unique.length });
      } else {
        failed += 1;
        const error =
          result.reason instanceof Error
            ? result.reason.message
            : String(result.reason || 'failed');
        failures.push({ path, error });
        onPathDone?.({ path, ok: false, error, completed, total: unique.length });
      }
    });
  }

  return { total: unique.length, ok, failed, failures };
}

export { APP_ROUTER_PATHS, APP_ROUTER_PATH_LIST, normalizePath };
